import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from '../../api/axiosApi';
import { useNavigate, useParams } from 'react-router-dom';
import { updatePainting } from '../../store/actions/paintingsActions';
import usePageTitle from '../../hooks/usePageTitle';

const AdminEditPainting = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]);
    const [currentImages, setCurrentImages] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    usePageTitle("Edit Painting | Dinara Fariz");

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const fetchPainting = async () => {
            try {
                const response = await axios.get(`/paintings/${id}`);
                const painting = response.data;

                setName(painting.name || '');
                setPrice(
                    painting.price
                        ? new Intl.NumberFormat('ru-RU').format(
                            String(painting.price).replace(/\D/g, '')
                        )
                        : ''
                );
                setDescription(painting.description || '');
                setCurrentImages(painting.images || []);
            } catch (err) {
                console.error(err);
                setError('Failed to load painting.');
            } finally {
                setLoading(false);
            }
        };

        fetchPainting();
    }, [id]);

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setImages((prev) => [...prev, ...newFiles]);
    };

    const handleRemoveNewImage = (indexToRemove) => {
        setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handlePriceChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, '');
        const formatted = new Intl.NumberFormat('ru-RU').format(numericValue);
        setPrice(formatted);
    };

    const getImageSrc = (img) => {
        if (!img) return '';
        if (typeof img === 'string') return img;
        return img.url || '';
    };

    const handleRemoveCurrentImage = (indexToRemove) => {
        setCurrentImages(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!name || !description) {
            setError('Please fill in all required fields.');
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('price', price ? Number(price.replace(/\s/g, '')) : '');
        formData.append('description', description);
        formData.append('currentImages', JSON.stringify(currentImages));

        images.forEach((image) => formData.append('images', image));

        try {
            await dispatch(updatePainting({ id, data: formData })).unwrap();

            navigate('/admin');
        } catch (err) {
            console.error(err);
            setError(typeof err === 'string' ? err : 'Failed to update painting.');
        }
    };

    if (loading) {
        return <div className="text-gray-500">Loading...</div>;
    }

    return (
        <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-[0.2em] text-gray-800 mb-8">
                Edit Painting
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                {error && <p className="text-sm text-red-600">{error}</p>}

                <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                        Title
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition"
                        required
                    />
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                        Price (KZT)
                    </label>
                    <input
                        type="text"
                        value={price}
                        onChange={handlePriceChange}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition"
                        placeholder="Optional"
                    />
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full border border-gray-300 px-4 py-3 text-sm min-h-[140px] resize-y focus:outline-none focus:border-black transition"
                        required
                    />
                </div>

                {currentImages.length > 0 && (
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
                            Current Images
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {currentImages.map((img, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={getImageSrc(img)}
                                        alt={`current ${index + 1}`}
                                        className="w-24 h-24 object-cover border border-gray-300"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCurrentImage(index)}
                                        className="absolute -top-2 -right-2 w-6 h-6 border border-gray-300 bg-white text-sm hover:bg-gray-100 transition"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>

                        <p className="text-xs text-gray-500 mt-3">
                            If you upload new images, the current ones will be replaced.
                        </p>
                    </div>
                )}

                <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                        Replace Images
                    </label>

                    <label
                        htmlFor="images"
                        className="block w-full border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 cursor-pointer hover:border-black hover:text-black transition"
                    >
                        Select one or more new images
                    </label>

                    <input
                        id="images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />
                </div>

                {images.length > 0 && (
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
                            New Images
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {images.map((img, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={URL.createObjectURL(img)}
                                        alt={`preview ${index + 1}`}
                                        className="w-24 h-24 object-cover border border-gray-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveNewImage(index)}
                                        className="absolute -top-2 -right-2 w-6 h-6 border border-gray-300 bg-white text-sm hover:bg-gray-100 transition"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="pt-2 flex gap-3 flex-wrap">
                    <button
                        type="submit"
                        className="border border-black px-6 py-3 text-sm uppercase tracking-[0.2em] text-black hover:bg-black hover:text-white transition"
                    >
                        Save Changes
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate('/admin')}
                        className="border border-gray-300 px-6 py-3 text-sm uppercase tracking-[0.2em] text-gray-700 hover:bg-gray-100 transition"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminEditPainting;